import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {Folder, initialAssetDisplayState, AssetDisplayState} from 'common/src/modules/Assets/Assets.types';
import updateFolderInTree from 'common/src/modules/Assets/store/shared/utils/updateFolderInTree';
import updateAssetInTree from 'common/src/modules/Assets/store/shared/utils/updateAssetInTree';
import removeAssetFromTree from 'common/src/modules/Assets/store/shared/utils/removeAssetFromTree';
import removeFolderFromTree from 'common/src/modules/Assets/store/shared/utils/removeFolderFromTree';

const initialState: AssetDisplayState = initialAssetDisplayState;

const contentFeedAssetsSlice = createSlice({
  name: 'contentFeedAssets',
  initialState,
  reducers: {
    clearContentFeedAssets: (state) => {
      state.assetTree = null;
      state.currentFolderId = null;
      state.selectedAssetIds = [];
      state.selectedFolderIds = [];
      state.searchTerm = '';
      state.searchResults = null;
      state.isLoadingTree = false;
      state.isLoadingFolderAssets = false;
      state.isSearching = false;
    },
    setAssetTree: (state, action: PayloadAction<Folder | null>) => {
      state.assetTree = action.payload;
    },
    setCurrentFolderId: (state, action: PayloadAction<number | null>) => {
      const previousFolderId = state.currentFolderId;
      state.currentFolderId = action.payload;

      if (previousFolderId !== action.payload) {
        state.selectedAssetIds = [];
        state.selectedFolderIds = [];
      }
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setSearchType: (state, action: PayloadAction<'name' | 'content'>) => {
      state.searchType = action.payload;
    },
    setSearchResults: (state, action: PayloadAction<Folder | null>) => {
      state.searchResults = action.payload;
    },
    clearSearch: (state) => {
      state.searchTerm = '';
      state.searchResults = null;
      state.isSearching = false;
    },
    setIsLoadingTree: (state, action: PayloadAction<boolean>) => {
      state.isLoadingTree = action.payload;
    },
    setIsLoadingFolderAssets: (state, action: PayloadAction<boolean>) => {
      state.isLoadingFolderAssets = action.payload;
    },
    setIsSearching: (state, action: PayloadAction<boolean>) => {
      state.isSearching = action.payload;
    },
    setSelectedAssetIds: (state, action: PayloadAction<number[]>) => {
      state.selectedAssetIds = action.payload;
    },
    setSelectedFolderIds: (state, action: PayloadAction<number[]>) => {
      state.selectedFolderIds = action.payload;
    },
    clearSelection: (state) => {
      state.selectedAssetIds = [];
      state.selectedFolderIds = [];
    },
    updateFolderName: (state, action: PayloadAction<{folderId: number; newName: string}>) => {
      const {folderId, newName} = action.payload;

      state.assetTree = updateFolderInTree(state.assetTree, folderId, {folderName: newName});

      if (state.searchResults) {
        state.searchResults = updateFolderInTree(state.searchResults, folderId, {folderName: newName});
      }
    },
    updateAssetName: (state, action: PayloadAction<{assetId: number; newName: string}>) => {
      const {assetId, newName} = action.payload;

      state.assetTree = updateAssetInTree(state.assetTree, assetId, {name: newName});

      if (state.searchResults) {
        state.searchResults = updateAssetInTree(state.searchResults, assetId, {name: newName});
      }
    },
    removeAsset: (state, action: PayloadAction<number>) => {
      const assetId = action.payload;

      state.assetTree = removeAssetFromTree(state.assetTree, assetId);

      if (state.searchResults) {
        state.searchResults = removeAssetFromTree(state.searchResults, assetId);
      }

      state.selectedAssetIds = state.selectedAssetIds.filter((id) => id !== assetId);
    },
    removeFolder: (state, action: PayloadAction<number>) => {
      const folderId = action.payload;

      state.assetTree = removeFolderFromTree(state.assetTree, folderId);

      if (state.searchResults) {
        state.searchResults = removeFolderFromTree(state.searchResults, folderId);
      }

      state.selectedFolderIds = state.selectedFolderIds.filter((id) => id !== folderId);
    },
  },
});

export const {
  clearContentFeedAssets,
  setAssetTree,
  setCurrentFolderId,
  setSelectedAssetIds,
  setSelectedFolderIds,
  clearSelection,
  setSearchTerm,
  setSearchType,
  setSearchResults,
  clearSearch,
  setIsLoadingTree,
  setIsLoadingFolderAssets,
  setIsSearching,
  updateFolderName,
  updateAssetName,
  removeAsset,
  removeFolder,
} = contentFeedAssetsSlice.actions;

export default contentFeedAssetsSlice.reducer;
